import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AlertTriangle } from "lucide-react";
import { useCruise } from "@/context/CruiseContext";
import { interpolateAlong } from "@/lib/geo";
import VesselMarker from "@/components/design-system/VesselMarker";

// Basemap presets per variant.
//
// The `voyage` preset uses Esri's **National Geographic** basemap —
// editorial cartography with warm relief, elegant typography, and
// cream-toned oceans that pair naturally with Cruise Command's
// Archivo/Poppins design language. An **OpenSeaMap** seamark overlay
// is layered on top; it is transparent at voyage-overview zoom and
// progressively reveals harbour buoys, marina icons, ferry lines and
// lighthouse marks as the traveler zooms into a port (nautical
// detail on demand — no configuration required).
//
// Esri's ArcGIS Online basemaps and OpenSeaMap tiles are both free
// for interactive web use with attribution.
//
// The `planning` and `memories` variants stay on the lighter CartoDB
// Voyager style so those workspaces retain their existing feel.
const NATGEO_ATTRIBUTION =
  "Tiles &copy; Esri &mdash; National Geographic, Esri, DeLorme, HERE, UNEP-WCMC, USGS, NASA, ESA, METI, NRCAN, GEBCO, NOAA, iPC";
const SEAMARK_ATTRIBUTION =
  "Seamarks &copy; OpenSeaMap contributors";
const CARTO_ATTRIBUTION =
  "&copy; OpenStreetMap contributors &copy; CARTO";

const TILE_PRESETS = {
  voyage: {
    // Esri National Geographic — editorial cartography basemap
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/NatGeo_World_Map/MapServer/tile/{z}/{y}/{x}",
    // OpenSeaMap seamark overlay — auto-reveals nautical detail at
    // higher zoom (harbours, buoys, ferry routes, lighthouses).
    overlayUrl: "https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png",
    attribution: `${NATGEO_ATTRIBUTION} &middot; ${SEAMARK_ATTRIBUTION}`,
    subdomains: null,
    maxZoom: 16,
    routeColor: "#c28e42",
    routeGlow: "rgba(194, 142, 66, 0.45)",
    routeWeight: 3.4,
    portFill: "#0f1e4a",
    portRing: "#ffffff",
    labelColor: "#1a2547",
    labelStroke: "rgba(255, 251, 240, 0.95)",
    bg: "#e9dfc7",
  },
  planning: {
    url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
    overlayUrl: null,
    attribution: CARTO_ATTRIBUTION,
    subdomains: "abcd",
    maxZoom: 18,
    routeColor: "#1e6de5",
    routeGlow: "rgba(30,109,229,0.35)",
    routeWeight: 2.6,
    portFill: "#1e6de5",
    portRing: "#ffffff",
    labelColor: "#0F172A",
    labelStroke: "rgba(255,255,255,0.9)",
    bg: "#e6f0fb",
  },
  memories: {
    url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
    overlayUrl: null,
    attribution: CARTO_ATTRIBUTION,
    subdomains: "abcd",
    maxZoom: 18,
    routeColor: "#22C55E",
    routeGlow: "rgba(34,197,94,0.35)",
    routeWeight: 2.6,
    portFill: "#22C55E",
    portRing: "#ffffff",
    labelColor: "#0F172A",
    labelStroke: "rgba(255,255,255,0.9)",
    bg: "#e6f0fb",
  },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

/**
 * Real-geography voyage map. Reads the itinerary + waypoints from
 * CruiseContext.voyage (ADR-006 — no hard-coded fallback).
 */
export default function GeoVoyageMap({
  variant = "voyage",
  height = 480,
  showShip = null,
  showLabels = true,
  fitPadding = [40, 40],
  interactive = true,
  className = "",
  focusPortId = null,
  scrubT = null,
  onPortClick = null,
  nextPortId = null,
}) {
  const preset = TILE_PRESETS[variant] ?? TILE_PRESETS.voyage;
  const shipEnabled = showShip ?? variant === "voyage";

  const { voyage, voyageError, polyline, portCalls, segments, activeSegmentIndex } = useCruise();

  // PD-033 · Maritime Route Visualization Standard.
  // We MUST NOT draw straight geodesic lines between ports — those cross
  // Mexico, Cuba, Florida, continents. If we don't have real maritime
  // approach_waypoints from the CKB, we simply don't draw a route line.
  // Port markers + ordinal day numbers communicate sequence without
  // implying impossible geography.
  const hasRealRoute = polyline && polyline.length >= 2;

  // BR-MAP-002 · Initial Viewport.
  // On first render the map MUST fit the bounds of the complete itinerary
  // — every port call AND every maritime waypoint we have — so the full
  // voyage is visible without user interaction. Previously we chose EITHER
  // the polyline OR the port coords which meant a leg without waypoints
  // could push the map off-screen. We now always union both sources.
  const boundsPoints = useMemo(() => {
    const seen = new Set();
    const pts = [];
    const push = (lat, lng) => {
      if (typeof lat !== "number" || typeof lng !== "number") return;
      const key = `${lat.toFixed(4)}:${lng.toFixed(4)}`;
      if (seen.has(key)) return;
      seen.add(key);
      pts.push([lat, lng]);
    };
    // Every port_call coordinate — sequence-independent, dedup-safe.
    for (const pc of portCalls || []) {
      const c = pc?.port?.coords;
      if (c) push(c.lat, c.lng);
    }
    // Every maritime waypoint we have.
    if (Array.isArray(polyline)) {
      for (const wp of polyline) {
        if (Array.isArray(wp) && wp.length === 2) push(wp[0], wp[1]);
      }
    }
    return pts;
  }, [polyline, portCalls]);

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const shipMarkerRef = useRef(null);

  const activeSegment = segments[Math.max(0, activeSegmentIndex)]?.waypoints ?? (hasRealRoute ? polyline : []);
  // When `scrubT` is externally controlled (e.g. by the full-page
  // Voyage Intelligence Map's timeline), the ship traces the entire
  // voyage polyline rather than the active segment. This lets the
  // traveler drag the scrubber from Day 1 → Day N and see the ship
  // glide across every leg.
  //
  // PD-033 exception (2026-02-20): if a voyage has NO maritime
  // approach_waypoints in the CKB, we still want to move the ship
  // marker between ports so the timeline scrubber has a visible ship
  // to animate. We build a coarse *ship-only* trajectory from ordered
  // port coordinates — this trajectory is used exclusively for the
  // ship's `<div>` position; the visible route polyline remains
  // undrawn (respecting the prohibition on straight geodesic lines
  // that cross land).
  const coarseTrajectory = useMemo(() => {
    if (hasRealRoute) return null;
    const seen = new Set();
    const pts = [];
    (portCalls || []).forEach((pc) => {
      const c = pc?.port?.coords;
      if (!c) return;
      const key = `${c.lat.toFixed(3)}:${c.lng.toFixed(3)}`;
      if (seen.has(key)) return;
      seen.add(key);
      pts.push([c.lat, c.lng]);
    });
    return pts.length >= 2 ? pts : null;
  }, [hasRealRoute, portCalls]);

  const shipTrajectory = scrubT != null
    ? (hasRealRoute ? polyline : coarseTrajectory) || activeSegment
    : (activeSegment && activeSegment.length >= 2 ? activeSegment : coarseTrajectory) || [];
  const [t, setT] = useState(0.58);
  const effectiveT = scrubT != null ? Math.min(1, Math.max(0, scrubT)) : t;

  // Set up the map once we have voyage geometry
  useEffect(() => {
    if (!containerRef.current) return undefined;
    // PD-033 · we render the map when EITHER we have real approach
    // waypoints OR at least two port coords to frame. What we never do
    // is draw straight lines between ports as a polyline.
    if (boundsPoints.length < 2) return undefined;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    if (containerRef.current._leaflet_id) {
      containerRef.current._leaflet_id = null;
    }

    const map = L.map(containerRef.current, {
      zoomControl: interactive,
      dragging: interactive,
      scrollWheelZoom: false,
      touchZoom: interactive,
      doubleClickZoom: interactive,
      boxZoom: interactive,
      keyboard: interactive,
      attributionControl: false,
      preferCanvas: false,
      zoomSnap: 0.25,
    });

    const tileOpts = { detectRetina: true, maxZoom: 18 };
    if (preset.subdomains) tileOpts.subdomains = preset.subdomains;
    L.tileLayer(preset.url, { ...tileOpts, maxZoom: preset.maxZoom || 18 }).addTo(map);

    // Nautical detail overlay (voyage variant). The seamark tiles are
    // transparent — invisible at voyage-overview zoom and progressively
    // reveal harbour buoys, marina icons, ferry lines, and lighthouse
    // marks as the traveler zooms into a port. No zoom gating needed.
    if (preset.overlayUrl) {
      L.tileLayer(preset.overlayUrl, {
        detectRetina: true,
        maxZoom: 18,
        opacity: 0.95,
        pane: "overlayPane",
      }).addTo(map);
    }

    // PD-033 · Route polyline ONLY when we have real maritime waypoints
    // from the CKB. Straight geodesic connectors are FORBIDDEN — they
    // cross Cuba, Mexico, Florida, continents. Ports + numbered day
    // markers below communicate sequence without implying impossible
    // geography.
    if (hasRealRoute) {
      L.polyline(polyline, {
        color: preset.routeColor,
        weight: preset.routeWeight,
        opacity: 0.95,
        dashArray: "6 8",
        lineCap: "round",
        lineJoin: "round",
        className: "cc-route-line",
      }).addTo(map);
    }

    // Port markers — de-duplicated by port_id so a round-trip home port only
    // renders one pin.
    const seen = new Set();
    portCalls.forEach((pc, idx) => {
      const port = pc.port;
      if (!port || !port.coords) return;
      if (seen.has(port.id)) return;
      seen.add(port.id);
      const coords = [port.coords.lat, port.coords.lng];
      // Vessel Marker directive · visual hierarchy for ports.
      // • Current port  → highlighted ring (blue outline · already existed).
      // • Visited ports → completed styling (muted teal fill · check-in look).
      // • Future ports  → default secondary port marker (small circle).
      const isCurrent =
        variant === "voyage" && activeSegmentIndex >= 0 &&
        segments[activeSegmentIndex]?.toPortCall?.port?.id === port.id;
      const isVisited =
        variant === "voyage" && activeSegmentIndex > 0 && !isCurrent && idx < activeSegmentIndex;
      const marker = L.circleMarker(coords, {
        radius: isCurrent ? 7 : (isVisited ? 5 : 5),
        color: isCurrent ? "#3b82f6" : (isVisited ? "#0F766E" : preset.portRing),
        fillColor: isCurrent ? "#ffffff" : (isVisited ? "#14B8A6" : preset.portFill),
        fillOpacity: isVisited ? 0.9 : 1,
        weight: isCurrent ? 3 : (isVisited ? 2 : 2),
        interactive: !!onPortClick,
        bubblingMouseEvents: false,
      }).addTo(map);

      // RC7-DASHBOARD-036 · Next-Port pulse.
      // Overlay a decorative divIcon at the same coords when this port
      // is the traveller's next stop. The base circleMarker stays
      // unchanged; the pulse layer is `interactive: false` so it never
      // steals a click from the underlying marker.
      const isNextPort = nextPortId && port.id === nextPortId;
      if (isNextPort) {
        const pulseIcon = L.divIcon({
          className: "cc-next-port-icon",
          html: '<div class="cc-next-port-pulse" data-testid="voyage-map-next-port-pulse" aria-hidden="true"></div>',
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });
        L.marker(coords, {
          icon: pulseIcon,
          interactive: false,
          keyboard: false,
          zIndexOffset: 500,
        }).addTo(map);
      }

      // When a click handler is provided, the entire marker becomes an
      // interactive Port Focus trigger. Cursor + hover ring signal it's
      // clickable even without a tooltip.
      if (onPortClick) {
        marker.on("click", () => onPortClick(pc));
        const el = marker.getElement && marker.getElement();
        if (el) el.style.cursor = "pointer";
      }

      if (showLabels) {
        const anchor = coords[1] > -89 ? "left" : "top";
        const dir = anchor === "left" ? "right" : "top";
        const offset = anchor === "left" ? [8, 0] : [0, -10];
        const dateLabel = fmtDate(pc.arrive_at || pc.depart_at);
        marker.bindTooltip(
          `<span class="cc-port-label" style="color:${preset.labelColor}; text-shadow: 0 0 6px ${preset.labelStroke}, 0 0 6px ${preset.labelStroke};">${port.name}</span><span class="cc-port-date" style="color:${preset.labelColor}; text-shadow: 0 0 6px ${preset.labelStroke}, 0 0 6px ${preset.labelStroke};">${dateLabel}</span>`,
          { permanent: true, direction: dir, offset, className: "cc-port-tooltip" },
        );
      }
    });

    // Ship marker
    if (shipEnabled && shipTrajectory && shipTrajectory.length >= 2) {
      const initial = interpolateAlong(shipTrajectory, effectiveT);
      // CC-ICON-001 v1.0 (RATIFIED · PO 2026-08-04) · Canonical Cruise
      // Command Vessel Marker. The marker is rendered from the frozen
      // `<VesselMarker />` design-system component; do not reimplement
      // its geometry. Per §4.1 the desktop map marker sizes at
      // 32–40 px — 40 px here for the map's primary focal element.
      // Per §4.2 the marker is centered on the geographic position
      // (iconAnchor = center), and only the inner `.cc-vessel` group
      // rotates with heading — the outer disc never rotates.
      const VESSEL_SIZE = 40;
      const vesselSvg = ReactDOMServer.renderToStaticMarkup(
        <VesselMarker heading={initial.heading} size={VESSEL_SIZE} />
      );
      const icon = L.divIcon({
        className: "cc-ship-icon",
        html: `<div class="cc-ship-wrap" data-testid="voyage-ship-marker">${vesselSvg}</div>`,
        iconSize: [VESSEL_SIZE, VESSEL_SIZE],
        iconAnchor: [VESSEL_SIZE / 2, VESSEL_SIZE / 2],
      });
      const marker = L.marker([initial.lat, initial.lon], { icon, interactive: false, zIndexOffset: 1000 }).addTo(map);
      shipMarkerRef.current = marker;
    }

    const bounds = L.latLngBounds(boundsPoints);
    map.fitBounds(bounds, { padding: fitPadding, animate: false });

    const onResize = () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
        mapRef.current.fitBounds(bounds, { padding: fitPadding, animate: false });
      }
    };
    window.addEventListener("resize", onResize);

    mapRef.current = map;

    return () => {
      window.removeEventListener("resize", onResize);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      shipMarkerRef.current = null;
    };
  }, [variant, interactive, showLabels, shipEnabled, hasRealRoute, portCalls.length, boundsPoints.length, activeSegmentIndex, shipTrajectory?.length, nextPortId]);

  // Ship animation — autonomous RAF drift only when NOT externally
  // controlled. When `scrubT` is provided, position comes entirely
  // from the parent (Voyage Intelligence Map timeline scrubber).
  useEffect(() => {
    if (!shipEnabled) return undefined;
    if (scrubT != null) return undefined;
    if (!activeSegment || activeSegment.length < 2) return undefined;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      setT((v) => {
        const next = v + dt * 0.02;
        return next > 0.95 ? 0.5 : next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [shipEnabled, activeSegment, scrubT]);

  useEffect(() => {
    if (!shipEnabled || !shipMarkerRef.current) return;
    if (!shipTrajectory || shipTrajectory.length < 2) return;
    const { lat, lon, heading } = interpolateAlong(shipTrajectory, effectiveT);
    shipMarkerRef.current.setLatLng([lat, lon]);
    // CC-ICON-001 §4.2 · "Never rotate the outer disc — only the inner
    // `.cc-vessel` group." Runtime updates target the .cc-vessel group
    // exactly as `<VesselMarker heading={…}>` does at initial render.
    const el = shipMarkerRef.current.getElement();
    if (el) {
      const vessel = el.querySelector(".cc-vessel");
      if (vessel) {
        vessel.style.transformOrigin = "16px 16px";
        vessel.style.transformBox = "view-box";
        vessel.style.transform = `rotate(${heading}deg)`;
      }
    }
  }, [effectiveT, shipTrajectory, shipEnabled]);

  // Focus the map on a specific port when `focusPortId` changes. Used by
  // the Voyage day-timeline overlay so selecting a day zooms the map to
  // that day's location. When `focusPortId` is null the map remains fit
  // to the full route.
  useEffect(() => {
    if (!mapRef.current) return;
    if (!focusPortId) {
      const bounds = L.latLngBounds(boundsPoints);
      if (bounds.isValid()) {
        mapRef.current.flyToBounds(bounds, { padding: fitPadding, duration: 0.6 });
      }
      return;
    }
    const pc = portCalls.find((p) => p?.port?.id === focusPortId);
    const coords = pc?.port?.coords;
    if (coords) {
      mapRef.current.flyTo([coords.lat, coords.lng], 6.5, { duration: 0.7 });
    }
  }, [focusPortId]);

  // ADR-006 fail-loud: no hard-coded fallback. Surface a clean error tile.
  if (voyageError || (!voyage && boundsPoints.length === 0)) {
    return (
      <div
        data-testid="cc-voyage-map-error"
        className={`cc-geo-map-wrap relative w-full overflow-hidden flex items-center justify-center ${className}`}
        style={{ height, background: preset.bg }}
      >
        <div className="max-w-md text-center p-6">
          <AlertTriangle className="w-6 h-6 mx-auto text-[#DC2626]" />
          <div className="mt-3 font-display font-bold text-[18px] text-[#0F172A]">
            Voyage data unavailable
          </div>
          <div className="mt-1 text-[13px] text-[#475569] leading-relaxed">
            The voyage record could not be loaded from <code className="font-mono">/api/cruises/current/workspace</code>.
            The map is intentionally not falling back to hard-coded coordinates — the voyage is now the authoritative source of truth (ADR-006).
          </div>
          {voyageError && (
            <div className="mt-3 text-[11px] font-mono text-[#DC2626]">
              {String(voyageError)}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`cc-geo-map-wrap relative w-full overflow-hidden ${className}`}
      style={{ height, background: preset.bg }}
      data-variant={variant}
      data-testid="cc-voyage-map"
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}
